import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { AccountFormValues } from "src/models/Account.model";
import {
    useEditAccountMutation,
    useGetAccountByIdQuery,
} from "src/services/AccountServices";
import { showToast } from "src/utils/showToaster";
import { array, object, string } from "yup";
import AccountForm from "../Layouts/AccountForm";

type Props = {
  onClose: () => void;
  accountId: string;
};

const EditAccountFormWrapper = ({ onClose, accountId }: Props) => {
  const [editAccount] = useEditAccountMutation();
  const { data, isLoading, isFetching } = useGetAccountByIdQuery({
    id: accountId,
  });
  const [accountInfo, setAccountInfo] = useState<any>();

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setAccountInfo(data?.data);
    }
  }, [data, isLoading, isFetching]);

  // Form Initial Values
  const initialValues: AccountFormValues = {
    accountName: accountInfo?.accountName || "",
    openingBalance: accountInfo?.openingBalance || "0",
    forAllBranches: accountInfo?.allBranches  || false,
    branches:
      accountInfo?.branchId?.map((branch: any) => {
        return {
          label: branch?.branchName,
          value: branch?._id,
        };
      }) || [],
    accountDetails: accountInfo?.accountDetails || "",
  };

  // Validation Schema
  const validationSchema = object().shape({
    accountName: string().required("Please enter account name"),
    accountDetails: string().required("Please enter account details"),
    branches: array().test(
      "branchTest",
      "Please select branch",
      (value, context) => {
        const { forAllBranches } = context.parent;
        return !forAllBranches ? (value && value?.length!==0) : true;
      }
    ),
  });

  // Handle Submit
  const handleSubmit = (
    values: AccountFormValues,
    { setSubmitting, resetForm }: FormikHelpers<AccountFormValues>
  ) => {
    const formattedValues = {
      accountName: values.accountName,
      openingBalance: values.openingBalance,
      branchId: values?.forAllBranches ? [] : values?.branches?.map((branch) => branch.value),
      accountDetails: values.accountDetails,
      allBranches:values?.forAllBranches
    };

    editAccount({
      body: formattedValues,
      id: accountId,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <AccountForm formikProps={formikProps} onClose={onClose} formType="Edit" />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditAccountFormWrapper;
